import { HttpClient } from '@angular/common/http';
import {Injectable, NgZone} from '@angular/core';
import {BehaviorSubject, Observable} from 'rxjs';
import {ScraperEvent} from '../types/ScraperEvent';
import {ScraperMessageTypeEnum} from '../enums/ScraperMessageTypeEnum';
import {ScraperTypeEnum} from '../enums/ScraperTypeEnum';
import {ApiHelper} from './api.helper';
import {ToolsService} from './tools.service';
import {AppConfigService} from "./app-config.service";

@Injectable()
export class ScraperService {
  private client: WebSocket;
  private trackingId: string;

  private eventsWritable: BehaviorSubject<ScraperEvent> = new BehaviorSubject(
    undefined
  );
  public readonly events: Observable<ScraperEvent> =
    this.eventsWritable.asObservable();
  private socketUrl: string;
  private scraper: ScraperTypeEnum;

  constructor(
    private http: HttpClient,
    private apiHelper: ApiHelper,
    private _zone: NgZone,
    private tools: ToolsService,
    private appConfigService: AppConfigService
  ) {
    this.socketUrl = `${this.appConfigService.appConfig.baseUrl.replace(
      /^https/,
      'wss'
    )}scraper/1177`;
  }

  public async getCareProvider(
    termsAccepted: boolean = false,
    scraper: ScraperTypeEnum = ScraperTypeEnum.GENERIC
  ) {
    await this.init(scraper, this.trackingId);
    this.sendCommand('careprovider', {termsAccepted: termsAccepted});
  }

  public stopEvents() {
    this.eventsWritable.next(undefined);
    if (!this.tools.isNil(this.client)) {
      this.client.close();
    }
  }

  public init(
    scraper: ScraperTypeEnum = ScraperTypeEnum.GENERIC,
    trackingId = null
  ): Promise<void> {
    this.scraper = scraper;
    this.trackingId = trackingId;

    return new Promise((resolve) => {
      this.client = new WebSocket(this.socketUrl);

      // connection open
      this.client.onopen = () => resolve();

      // events
      this.client.onmessage = (event) => {
        const message = JSON.parse(event.data) as ScraperEvent;

        if (message.messageType === ScraperMessageTypeEnum.TRACKING_ID) {
          this.trackingId = message.message;
        } else {
          this._zone.run(() => {
            this.eventsWritable.next(message);
          });
        }
      };

      // error
      this.client.onerror = (e) => {
        this.trackingId = null;
        this.eventsWritable.error(e);
      };
    });
  }

  public registerInit(
    careProviderName: string,
    termsAccepted: boolean = false,
    wishes?: string
  ): void {
    this.sendCommand('register', {
      step: '1',
      newCareProvider: careProviderName,
      termsAccepted: termsAccepted,
      wishes,
    });
  }

  public registerConfirm(
    careProviderName: string,
    wishes?: string,
    doctorId?: string
  ) {
    this.sendCommand('register', {
      step: '2',
      newCareProvider: careProviderName,
      wishes,
      doctorId,
    });
  }

  private sendCommand(
    command: string,
    params?:
      | {
      step: string;
      newCareProvider: string;
      wishes?: string;
      termsAccepted?: boolean;
      doctorId?: string;
    }
      | { termsAccepted?: boolean }
  ): void {
    if (!this.client) {
      console.error(
        'You are trying to send the command before initializing the client!'
      );
      return;
    }

    this.client.send(
      JSON.stringify({
        action: command,
        locationHref: window.location.href,
        scraper: this.scraper,
        params,
        trackingId: this.trackingId,
      })
    );
  }
}
